import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { Bucket, BreakdownResponse, BreakdownRow, BreakdownSort } from '../../services/executionStatsService';
import {
    BUCKET_LABELS, FAILURE_RATE_DEFINITION, NOT_TRACKED, TOTAL_DEFINITION, bucketDefinition, count, countInk,
    percent, relativeTime
} from './executionStatsVocabulary';
import { dsVar, ink } from '../../theme/tokens';

interface StatsBreakdownTableProps {
    data: BreakdownResponse;
    sort: BreakdownSort;
    onSort: (sort: BreakdownSort) => void;
    /** Opens the runs behind a cell; `null` bucket is every run. */
    onSelect: (row: BreakdownRow, bucket: Bucket | null) => void;
}

interface Column {
    id: string;
    label: string;
    definition: string;
    bucket?: Bucket | null;
    sort?: BreakdownSort;
}

const cell = { px: 1.5, py: 1, borderBottom: `1px solid ${dsVar('line')}`, fontSize: '0.8125rem', textAlign: 'right' as const };

/**
 * One row per flow or agent node, worst first.
 *
 * Sorted by failed runs by default, so the rows that need attention are the
 * ones on screen. Every count opens the runs behind it.
 */
const StatsBreakdownTable: React.FC<StatsBreakdownTableProps> = ({ data, sort, onSort, onSelect }) => {
    const columns: Column[] = [
        { id: 'total', label: 'Runs', definition: TOTAL_DEFINITION, bucket: null, sort: 'total' },
        { id: 'success', label: BUCKET_LABELS.success, definition: bucketDefinition('success', data.staleAfterHours), bucket: 'success' },
        { id: 'failed', label: BUCKET_LABELS.failed, definition: bucketDefinition('failed', data.staleAfterHours), bucket: 'failed', sort: 'failed' },
        { id: 'failureRate', label: 'Failure rate', definition: FAILURE_RATE_DEFINITION, sort: 'failureRate' },
        ...(data.tracked.pendingReview
            ? [{ id: 'pendingReview', label: BUCKET_LABELS.pendingReview, definition: bucketDefinition('pendingReview', data.staleAfterHours), bucket: 'pendingReview' as Bucket, sort: 'pendingReview' as BreakdownSort }]
            : []),
        ...(data.tracked.running
            ? [
                { id: 'running', label: BUCKET_LABELS.running, definition: bucketDefinition('running', data.staleAfterHours), bucket: 'running' as Bucket },
                { id: 'stalled', label: BUCKET_LABELS.stalled, definition: bucketDefinition('stalled', data.staleAfterHours), bucket: 'stalled' as Bucket }
            ]
            : []),
        { id: 'lastRunAt', label: 'Last run', definition: 'When the latest run in this range started.' }
    ];

    if (!data.rows.length) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                No runs started in this range.
            </Typography>
        );
    }

    const renderValue = (row: BreakdownRow, column: Column) => {
        if (column.id === 'failureRate') {
            return <span style={{ color: row.failureRate ? ink('#F87171') : dsVar('fg-muted') }}>{percent(row.failureRate)}</span>;
        }
        if (column.id === 'lastRunAt') {
            return <span style={{ color: dsVar('fg-2') }} title={row.lastRunAt ?? undefined}>{relativeTime(row.lastRunAt)}</span>;
        }
        const value = column.id === 'total' ? row.total : row[column.bucket as Bucket];
        if (value === null) return <span style={{ color: dsVar('fg-muted'), fontStyle: 'italic' }}>{NOT_TRACKED}</span>;
        const tone = column.bucket ? ink(countInk(column.bucket, value)) : dsVar('fg');
        if (!value) return <span style={{ color: dsVar('fg-faint') }}>0</span>;
        return (
            <Box
                component="button"
                onClick={() => onSelect(row, column.bucket ?? null)}
                sx={{
                    background: 'none', border: 'none', p: 0, cursor: 'pointer', font: 'inherit', color: tone,
                    fontWeight: column.id === 'total' || column.bucket === 'failed' ? 700 : 400,
                    '&:hover': { textDecoration: 'underline' }
                }}
            >
                {count(value)}
            </Box>
        );
    };

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <Box component="th" sx={{ ...cell, textAlign: 'left', color: dsVar('fg-2'), fontWeight: 600, fontSize: '0.75rem' }}>
                            Name
                        </Box>
                        {columns.map((column) => (
                            <Box
                                key={column.id}
                                component="th"
                                aria-sort={column.sort && column.sort === sort ? 'descending' : undefined}
                                sx={{ ...cell, color: column.sort === sort ? dsVar('accent-fg') : dsVar('fg-2'), fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                            >
                                <Tooltip title={column.definition} arrow placement="top">
                                    {column.sort ? (
                                        <Box
                                            component="button"
                                            onClick={() => onSort(column.sort!)}
                                            sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                                        >
                                            {column.label}{column.sort === sort ? ' ↓' : ''}
                                        </Box>
                                    ) : (
                                        <span style={{ cursor: 'help' }}>{column.label}</span>
                                    )}
                                </Tooltip>
                            </Box>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.rows.map((row) => (
                        <Box component="tr" key={row.key} sx={{ '&:hover': { backgroundColor: dsVar('fg-2', 0.05) } }}>
                            <Box component="td" sx={{ ...cell, textAlign: 'left', maxWidth: 320 }}>
                                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body'), fontWeight: 600 }} noWrap>
                                    {row.name || row.key}
                                </Typography>
                                <Typography sx={{ fontSize: '0.625rem', color: dsVar('fg-muted'), fontFamily: '"JetBrains Mono", monospace' }} noWrap title={row.key}>
                                    {row.key}
                                </Typography>
                            </Box>
                            {columns.map((column) => (
                                <Box component="td" key={column.id} sx={cell}>
                                    {renderValue(row, column)}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </tbody>
            </Box>
        </Box>
    );
};

export default StatsBreakdownTable;
